import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Alert,
  DialogContent,
  TextField,
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  DialogActions,
  Stack,
} from '@mui/material';
import apiService from '../../services/api';
import { Close as CloseIcon } from '@mui/icons-material';

const Title = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
    {' '}
    {children}
  </Typography>
);

const ExhibitorDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exhibitor, setExhibitor] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [boothNumber, setBoothNumber] = useState('');
  const [boothType, setBoothType] = useState<'shell_space' | 'raw_space'>(
    'shell_space'
  );

  const [boothNumberError, setBoothNumberError] = useState('');
  const [boothTypeError, setBoothTypeError] = useState('');
  const [approveError, setApproveError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.getExhibitorById(Number(id));
      setExhibitor(response);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const openApproveDialog = () => {
    setIsEditMode(false);
    setBoothNumber('');
    setBoothType('shell_space');
    setApproveDialogOpen(true);
  };

  const openEditDialog = () => {
    setIsEditMode(true);
    setBoothNumber(exhibitor.boothNumber || '');
    setBoothType(exhibitor.boothType || 'shell_space');
    setApproveDialogOpen(true);
  };

  const handleApproveExhibitor = async () => {
    let hasError = false;

    if (!boothNumber.trim()) {
      setBoothNumberError('Booth number is required');
      hasError = true;
    }

    if (!boothType) {
      setBoothTypeError('Booth type is required');
      hasError = true;
    }

    if (hasError) return;

    try {
      setActionLoading(true);

      if (isEditMode) {
        await apiService.updateExhibitorBooth(exhibitor.id, {
          boothNumber,
          boothType,
        });
      } else {
        await apiService.approveExhibitor(exhibitor.id, {
          boothNumber,
          boothType,
        });
      }

      setApproveDialogOpen(false);
      setIsEditMode(false);
      fetchDetails();
    } catch (err: any) {
      setApproveError(err.response?.data?.message || 'Operation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const resetApproveForm = () => {
    setBoothNumber('');
    setBoothType('shell_space'); // initial value
    setBoothNumberError('');
    setBoothTypeError('');
    setApproveError('');
  };

  const maskId = (value?: string) => {
    if (!value || value.length <= 4) return value || 'N/A';
    const last4 = value.slice(-4);
    const masked = '*'.repeat(value.length - 4);
    return masked + last4;
  };

  if (loading || !exhibitor)
    return (
      <Typography sx={{ p: 3 }}>
        <CircularProgress />{' '}
      </Typography>
    );

  const handleClose = () => {
    if (!loading) {
      setApproveDialogOpen(false);
      setIsEditMode(false);
      resetApproveForm();
    }
  };

  return (
    <>
      <Box sx={{ p: 4 }}>
        <Button variant="outlined" onClick={() => navigate('/exhibitors')}>
          Back to List
        </Button>
        {/* PAGE HEADER WITH IMAGE */}
        <Grid container spacing={2} alignItems="center" sx={{ mt: 3, mb: 4 }}>
          {/* Left: Title + Status */}
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight={700}>
              Exhibitor Profile
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <Typography
                fontWeight={600}
                color="text.secondary"
                variant="body2"
              >
                Approval Status:
              </Typography>

              <Chip
                label={exhibitor.isVerified ? 'Approved' : 'Pending'}
                color={exhibitor.isVerified ? 'success' : 'warning'}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Grid>

          {/* Right: Actions */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              gap: 1,
              mt: { xs: 1, md: 0 },
            }}
          >
            {!exhibitor.isVerified && !exhibitor.boothNumber && (
              <Button
                variant="contained"
                color="success"
                onClick={openApproveDialog}
              >
                Approve
              </Button>
            )}

            {exhibitor.boothNumber && (
              <Button variant="outlined" onClick={openEditDialog}>
                Edit Booth
              </Button>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />
        {/* Company Details */}
        <Title>Company Details</Title>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Company / Fascia Name:</strong> {exhibitor.fasciaName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Address:</strong> {exhibitor.companyAddress ?? 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>GST Number:</strong>{' '}
              {exhibitor.gstin ? maskId(exhibitor.gstin) : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>PAN Number:</strong>{' '}
              {exhibitor.companyPan ? maskId(exhibitor.companyPan) : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Office Number:</strong> {exhibitor.officeNumber ?? 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        <Title>Booth Details</Title>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Booth Number:</strong> {exhibitor.boothNumber ?? 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Booth Type:</strong> {exhibitor.boothType ?? 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {/* Type of Organization */}
        <Title>Type of Organization</Title>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={exhibitor.isStartupCompany ? 'Startup' : 'Corporate'} />
        </Box>

        {/* Categories */}
        <Title>Business Categories</Title>
        {exhibitor.categories.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {exhibitor.categories?.map((cat: any) => (
              <Chip
                key={cat.category.id}
                label={cat.category.name}
                sx={{
                  background: cat.category.categoryColor,
                  color: '#050404',
                }}
              />
            ))}
          </Box>
        )}

        {/* Major Export Country */}
        <Title>Major Export Country</Title>
        <Typography>{exhibitor.majorExportMarkets ?? 'N/A'}</Typography>

        {/* Product Range */}
        <Title>Product Range & Area of Specialisation</Title>
        <Typography>{exhibitor.productRangeSpecialisation ?? 'N/A'}</Typography>

        {/* International Clients */}
        <Title>Major International Clients / Projects</Title>
        <Typography>{exhibitor.majorInternationalClients ?? 'N/A'}</Typography>

        {/* New Products */}
        <Title>New Product Launch</Title>
        <Typography>{exhibitor.newProductsLaunch ?? 'N/A'}</Typography>

        {/* Company Profile */}
        <Title>Company Profile</Title>
        <Typography sx={{ whiteSpace: 'pre-line' }}>
          {exhibitor.companyProfile ?? 'N/A'}
        </Typography>

        {/* Contact Details */}
        <Title>Contact Details</Title>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Representative:</strong> {exhibitor.salutation}{' '}
              {exhibitor.representativeName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Designation:</strong>{' '}
              {exhibitor.representativeDesignation || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Email:</strong> {exhibitor.user?.email}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Phone:</strong> {exhibitor.user?.phone ?? 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {/* Status */}
        <Title>Status</Title>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={exhibitor.user?.isActive ? 'Active' : 'Inactive'}
            color={exhibitor.user?.isActive ? 'success' : 'error'}
          />
          <Chip
            label={exhibitor.isVerified ? 'Verified' : 'Not Verified'}
            color={exhibitor.isVerified ? 'success' : 'warning'}
          />
          <Chip
            label={exhibitor.isFeatured ? 'Featured Exhibitor' : 'Standard'}
            color={exhibitor.isFeatured ? 'primary' : 'default'}
          />
        </Box>
      </Box>

      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, position: 'relative' }}>
          {/* Approve Exhibitor */}
          {isEditMode ? 'Edit Booth Details' : 'Approve Exhibitor'}

          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {approveError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Booth Number"
              required
              fullWidth
              value={boothNumber}
              error={Boolean(boothNumberError)}
              helperText={boothNumberError}
              onChange={e => {
                setBoothNumber(e.target.value);
                if (boothNumberError) setBoothNumberError('');
              }}
              placeholder="Enter booth number"
            />

            <FormControl fullWidth required error={Boolean(boothTypeError)}>
              <InputLabel>Booth Type</InputLabel>
              <Select
                label="Booth Type"
                value={boothType}
                onChange={e => {
                  setBoothType(e.target.value as 'shell_space' | 'raw_space');
                  if (boothTypeError) setBoothTypeError('');
                }}
              >
                <MenuItem value="shell_space">Shell Space</MenuItem>
                <MenuItem value="raw_space">Raw Space</MenuItem>
              </Select>

              {boothTypeError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {boothTypeError}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveExhibitor}
            disabled={actionLoading}
          >
            {isEditMode ? 'Update' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExhibitorDetailsPage;
